import { Controller } from "stimulus";
import * as bootstrap from "bootstrap";

export default class extends Controller {
  static targets = [
    "modal",
    "title",
    "content",
    "grid",
    "loader",
    "scenarioAttachmentId",
  ];

  initialize() {
    console.log("Attachment controller initialized");
  }

  connect() {
    this.currentPage = 1;
    this.loading = false;
    this.hasMore = true;
    console.log("Attachment controller connected");

    // Initialize the modal
    const modalElement = this.element.querySelector(
      '[data-target="attachment.modal"]'
    );
    if (modalElement) {
      this.modal = new bootstrap.Modal(modalElement);
      console.log("Modal initialized:", this.modal);
    } else {
      console.error("Modal element not found");
    }
  }

  disconnect() {
    console.log("Attachment controller disconnected");
    if (this.modal) {
      this.modal.dispose();
      this.modal = null;
    }
  }

  show(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (button && button.dataset.attachmentType) {
      const type = button.dataset.attachmentType;
      console.log("Modal showing with type:", type);
      this.setupModal(type);
    }
  }

  showUploadModal(event) {
    event.preventDefault();
    const uploadModal = document.getElementById("upload_modal");
    if (uploadModal) {
      const modal = new bootstrap.Modal(uploadModal);
      modal.show();
    }
  }

  setupModal(type) {
    if (!this.modal) {
      console.error("Modal not initialized");
      return;
    }

    let title = "Add Attachment";
    switch (type) {
      case "ecg":
        title = "Add ECG";
        break;
      case "xray":
        title = "Add X-Ray";
        break;
      case "simulation_picture":
        title = "Add Simulation Picture";
        break;
    }

    const titleElement = document.querySelector(
      '[data-target="attachment.title"]'
    );
    if (titleElement) {
      titleElement.textContent = title;
    }

    this.resetAndFetchAttachments(type);
    this.modal.show();
  }

  save(event) {
    event.preventDefault();
    // Save logic here
    if (this.modal) {
      this.modal.hide();
    }
  }

  resetAndFetchAttachments(type) {
    this.currentPage = 1;
    this.loading = false;
    this.hasMore = true;
    this.attachmentType = type;

    if (this.contentTarget) {
      this.contentTarget.innerHTML = `
        <div class="container-fluid" style="height: 70vh; overflow-y: auto;" data-action="scroll->attachment#onScroll">
          <div class="row row-cols-1 row-cols-md-4 g-4" data-target="attachment.grid"></div>
          <div class="text-center mt-3" data-target="attachment.loader" style="display: none;">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      `;
      this.fetchAttachments();
    }
  }

  async fetchAttachments() {
    if (this.loading || !this.hasMore) return;

    this.loading = true;
    this.loaderTarget.style.display = "block";

    try {
      const response = await fetch(
        `/scenario_attachments/get_attachments?type=${this.attachmentType}&page=${this.currentPage}`
      );
      const data = await response.json();

      data.attachments.forEach((attachment) => {
        const col = document.createElement("div");
        col.className = "col";
        col.innerHTML = `
          <div class="card h-100">
            <div class="card-img-wrapper" 
                 style="height: 300px; overflow: hidden; display: flex; align-items: center; background: #f8f9fa; cursor: pointer;" 
                 data-controller="attachment"
                 data-action="click->attachment#showFullImage"
                 data-full-size="${attachment.full_size}"
                 data-content-type="${attachment.content_type}">
              <img src="${attachment.thumbnail}" 
                   class="card-img-top" 
                   alt="${attachment.name}"
                   style="width: 100%; height: auto; object-fit: contain;">
            </div>
            <div class="card-body">
              <h5 class="card-title">${attachment.name}</h5>
              <button class="btn btn-primary btn-sm" 
                      data-action="click->attachment#selectAttachment" 
                      data-attachment-id="${attachment.id}"
                      data-attachment-name="${attachment.name}">
                Select
              </button>
            </div>
          </div>
        `;
        this.gridTarget.appendChild(col);
      });

      this.hasMore = data.has_more;
      this.currentPage++;
    } catch (error) {
      console.error("Error fetching attachments:", error);
      const errorDiv = document.createElement("div");
      errorDiv.className = "alert alert-danger";
      errorDiv.textContent = "Error loading attachments";
      this.contentTarget.appendChild(errorDiv);
    } finally {
      this.loading = false;
      this.loaderTarget.style.display = this.hasMore ? "block" : "none";
    }
  }

  onScroll(event) {
    const container = event.currentTarget;
    if (
      container.scrollHeight - container.scrollTop <=
      container.clientHeight + 100
    ) {
      this.fetchAttachments();
    }
  }

  setupXRayContent() {
    this.contentTarget.innerHTML = `
      <div class="container-fluid">
        <!-- Add your X-Ray-specific content here -->
      </div>
    `;
  }

  setupOtherContent() {
    this.contentTarget.innerHTML = `
      <div class="container-fluid">
        <!-- Add your Other attachment content here -->
      </div>
    `;
  }

  showFullImage(event) {
    event.preventDefault();
    event.stopPropagation();

    const wrapper = event.currentTarget;
    const fullSizeUrl = wrapper.dataset.fullSize;
    const contentType = wrapper.dataset.contentType;
    const imgElement = wrapper.querySelector("img");

    const overlay = document.createElement("div");
    overlay.className =
      "position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center";
    overlay.style.cssText = `
      background: rgba(0,0,0,0.7); 
      z-index: 9999;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      padding: 2rem;
    `;

    if (contentType === "application/pdf") {
      const content = `
        <div class="position-relative bg-white rounded shadow-lg" style="width: 85vw; height: 85vh;">
          <button class="btn btn-outline-dark position-absolute top-0 end-0 m-2" 
                  style="z-index: 10000;"
                  onclick="this.closest('.position-fixed').remove()">
            <i class="fas fa-times"></i> Close
          </button>
          <iframe src="${fullSizeUrl}" 
                  style="width: 100%; height: 100%; border: none; border-radius: 0.375rem;"
                  title="PDF Viewer">
          </iframe>
        </div>
      `;
      overlay.innerHTML = content;
    } else {
      const content = `
        <div class="position-relative bg-white p-2 rounded shadow-lg" style="max-width: 85vw; max-height: 85vh;">
          <button class="btn btn-outline-dark position-absolute top-0 end-0 m-2" 
                  style="z-index: 10000;"
                  onclick="this.closest('.position-fixed').remove()">
            <i class="fas fa-times"></i> Close
          </button>
          <img src="${fullSizeUrl}" 
               class="img-fluid rounded" 
               style="max-height: 85vh; max-width: 85vw; width: auto; display: block;"
               alt="${imgElement.alt}">
        </div>
      `;
      overlay.innerHTML = content;
    }

    document.body.appendChild(overlay);

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) {
        overlay.remove();
      }
    });
  }

  selectAttachment(event) {
    console.log("=== selectAttachment called ===");
    console.log("Event target:", event.target);
    console.log("Current target:", event.currentTarget);
    console.log("Dataset:", event.currentTarget.dataset);

    event.preventDefault();
    const attachmentId = event.currentTarget.dataset.attachmentId;
    console.log("Attachment ID:", attachmentId);

    const card = event.currentTarget.closest(".card");
    console.log("Card element:", card);

    const attachmentName = card.querySelector(".card-title").textContent;
    console.log("Attachment name:", attachmentName);

    const instance = event.target.closest(".nested-fields");
    console.log("Nested fields instance:", instance);

    if (instance) {
      console.log("=== Found instance ===");
      const inputField = instance.querySelector(".att-id");
      const nameField = instance.querySelector(".att-name");
      const attachmentActions = instance.querySelector(".attachment-actions");

      console.log("Input field:", inputField);
      console.log("Name field:", nameField);
      console.log("Attachment actions:", attachmentActions);

      if (inputField) {
        inputField.value = attachmentId;
        console.log("Set input field value to:", attachmentId);

        if (nameField) {
          nameField.textContent = attachmentName;
          console.log("Set name field text to:", attachmentName);
        }

        if (attachmentActions) {
          attachmentActions.style.display = "none";
          console.log("Hidden attachment actions");
        }

        // Trigger change event
        const changeEvent = new Event("change", { bubbles: true });
        inputField.dispatchEvent(changeEvent);
        console.log("Dispatched change event");
      } else {
        console.error("Input field not found in instance");
      }

      // Close the modal
      if (this.modal) {
        this.modal.hide();
        console.log("Modal hidden");
      } else {
        console.error("Modal not found");
      }
    } else {
      console.error("Instance not found");
    }
  }

  previewUpload(event) {
    const file = event.target.files[0];
    const preview = document.getElementById(`${this.element.id}_preview`);
    const previewContainer = preview.parentElement;

    if (file) {
      const reader = new FileReader();
      reader.onload = function (e) {
        preview.src = e.target.result;
        previewContainer.style.display = "block";
      };
      reader.readAsDataURL(file);
    }
  }

  async uploadToLibrary(event) {
    event.preventDefault();
    const form = this.uploadFormTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch("/scenario_attachments", {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Update the hidden field with the new attachment ID
        this.scenarioAttachmentIdTarget.value = data.id;
        // Close the modal
        const modal = bootstrap.Modal.getInstance(this.element);
        modal.hide();
        // Refresh the page or update the UI as needed
        window.location.reload();
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file. Please try again.");
    }
  }
}
